import { Calculator, Mail, Phone, MapPin, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { generateLocalizedUrl, type PageSlug } from "../lib/slugUtils";

const Footer = () => {
  const { t, i18n } = useTranslation('footer');
  const currentLang = i18n.language;
  return (
    <footer className="bg-navy-primary text-primary-foreground py-12 border-t border-golden-primary/20">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-accent-foreground" />
              </div>
              <span className="text-xl font-bold">
                PRICE<span className="text-accent">FORMS</span>
              </span>
            </div>
            <p className="text-primary-foreground/80 mb-4 max-w-md">
              {t('brand.description')}
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 text-primary-foreground/70">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${t('contact.email')}`} className="hover:text-accent transition-smooth">
                  {t('contact.email')}
                </a>
              </div>
              <div className="flex items-center space-x-3 text-primary-foreground/70">
                <Phone className="w-4 h-4" />
                <a href={`tel:${t('contact.phone')}`} className="hover:text-accent transition-smooth">
                  {t('contact.phone')}
                </a>
              </div>
              <div className="flex items-center space-x-3 text-primary-foreground/70">
                <MapPin className="w-4 h-4" />
                <span>{t('contact.address')}</span>
              </div>
              <div className="flex items-center space-x-3 text-primary-foreground/70">
                <Clock className="w-4 h-4" />
                <div className="flex flex-col">
                  <span className="text-sm">{t('contact.workingHours')}</span>
                  <span className="text-sm">{t('contact.weekendHours')}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h3 className="font-semibold mb-4 text-accent">{t('services.title')}</h3>
            <ul className="space-y-2 text-primary-foreground/70">
              {(() => {
                const items = t('services.items', { returnObjects: true });
                return Array.isArray(items) ? items.map((item: string, index: number) => {
                   // Special handling for expert service links
                   if (item.toLowerCase().includes('jotform')) {
                     return (
                       <li key={index}>
                         <Link to={generateLocalizedUrl('jotform-expert', currentLang)} className="hover:text-accent transition-smooth">{item}</Link>
                       </li>
                     );
                   }
                   if (item.toLowerCase().includes('fillout')) {
                     return (
                       <li key={index}>
                         <Link to={generateLocalizedUrl('fillout-expert', currentLang)} className="hover:text-accent transition-smooth">{item}</Link>
                       </li>
                     );
                   }
                    return (
                      <li key={index}>
                        <Link to={generateLocalizedUrl('services', currentLang)} className="hover:text-accent transition-smooth">{item}</Link>
                      </li>
                    );
                }) : [];
              })()}
            </ul>
          </div>
          
          {/* Industries */}
          <div>
            <h3 className="font-semibold mb-4 text-accent">{t('industries.title')}</h3>
            <ul className="space-y-2 text-primary-foreground/70">
              {(() => {
                const industries: Array<{ slug: PageSlug; translationKey: string }> = [
                  { slug: 'industry-real-estate', translationKey: 'industries.items.0' },
                  { slug: 'industry-ecommerce', translationKey: 'industries.items.1' },
                  { slug: 'industry-restaurants', translationKey: 'industries.items.2' },
                  { slug: 'industry-financial', translationKey: 'industries.items.3' },
                  { slug: 'industry-agencies', translationKey: 'industries.items.4' },
                  { slug: 'industry-construction', translationKey: 'industries.items.5' }
                ];
                
                return industries.map((industry) => {
                  const label = t(industry.translationKey, { defaultValue: '' });
                  if (!label) return null;
                  
                  return (
                    <li key={industry.slug}>
                      <Link 
                        to={generateLocalizedUrl(industry.slug, currentLang)} 
                        className="hover:text-accent transition-smooth"
                        data-testid={`link-industry-${industry.slug}`}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                }).filter(Boolean);
              })()}
            </ul>
          </div>
          
          {/* Company */}
          <div>
            <h3 className="font-semibold mb-4 text-accent">{t('company.title')}</h3>
            <ul className="space-y-2 text-primary-foreground/70">
               {(() => {
                 const items = t('company.items', { returnObjects: true });
                 return Array.isArray(items) ? [
                   <li key={0}><Link to={generateLocalizedUrl('about', currentLang)} className="hover:text-accent transition-smooth">{items[0]}</Link></li>,
                   <li key={1}><Link to={generateLocalizedUrl('portfolio', currentLang)} className="hover:text-accent transition-smooth">{items[1]}</Link></li>,
                   <li key={2}><Link to={`/${currentLang}/#process`} className="hover:text-accent transition-smooth">{items[2]}</Link></li>,
                   <li key={3}><Link to={generateLocalizedUrl('contact', currentLang)} className="hover:text-accent transition-smooth">{items[3]}</Link></li>
                 ] : [];
               })()}
            </ul>
          </div>
        </div>
        
        <div className="border-t border-primary-foreground/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <p className="text-primary-foreground/70 text-sm">
                {t('business.name')} · {t('business.cvr')}
              </p>
              <div className="flex space-x-4 text-sm">
                <a href="/terms" className="text-primary-foreground/70 hover:text-accent transition-smooth">
                  {t('legal.terms')}
                </a>
                <a href="/privacy" className="text-primary-foreground/70 hover:text-accent transition-smooth">
                  {t('legal.privacy')}
                </a>
              </div>
            </div>
            <p className="text-primary-foreground/60 text-sm">
              {t('copyright')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;